import "./ClipboardView.css";
import { Button } from "@ui/Button.tsx";
import { Card } from "@ui/Card";
import { Counter } from "@ui/Counter";
import { useLayoutAppKind } from "@ui/layouts/layoutProvider";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { toast } from "@ui/molecules/Toasts/toast";
import { HStack, Stack, VStack } from "@ui/Stack";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { TextMono } from "@ui/typography/TextMono.tsx";
import { createSignal, For, Show } from "solid-js";
import {
	mentionPromptFromQuestionBoxMentions,
	writeAskQuestionStashFor,
} from "~/modules/shared/ask/askQuestionStash";
import { addMentionsToBoard } from "~/modules/shared/boards/addMentionsToBoard";
import { useBoards } from "~/modules/shared/boards/boardsContext";
import { SelectBoardPanel } from "~/modules/shared/boards/SelectBoardPanel";
import { triggerFileDownload } from "~/modules/shared/utils/triggerFileDownload";
import { VideoMentionPill } from "../VideoMentionPill.tsx";
import { askPageForAppKind } from "./askPageForAppKind.ts";
import { clipboardCsv, clipboardCsvFilename } from "./clipboardCsv.ts";
import { clipboardQuestionBoxMentions } from "./clipboardMentions.ts";
import {
	clearClipboard,
	clipboardCount,
	clipboardVideoMentions,
	removeVideoMentionFromClipboard,
} from "./clipboardStore.ts";

type Props = {
	/** Insert the clipboard's contents into the QuestionBox at the caret. */
	onPasteContext?: () => void;
	/** Project the boards on offer belong to. */
	projectId?: string;
	/** False for a signed-out reader, who has no boards. */
	canAddToBoard?: boolean;
};

export const ClipboardView = (props: Props) => {
	const appKind = useLayoutAppKind();
	const boards = useBoards();
	const [boardPanelOpen, setBoardPanelOpen] = createSignal(false);
	const askPage = askPageForAppKind(appKind, props.projectId);

	const startNewQuestion = (href: string) => {
		const prompt = mentionPromptFromQuestionBoxMentions(
			clipboardQuestionBoxMentions(),
		);
		if (prompt === "") {
			return;
		}
		writeAskQuestionStashFor("prompt", prompt);
		window.location.href = href;
	};

	const downloadClipList = () => {
		const csv = clipboardCsv(
			clipboardVideoMentions().map((entity) => entity.data),
		);
		triggerFileDownload(
			new Blob([csv], { type: "text/csv;charset=utf-8" }),
			clipboardCsvFilename(),
		);
	};

	const addClipboardToBoard = async (board: {
		id: string;
		title?: string | null;
	}) => {
		const mentions = clipboardVideoMentions().map((entity) => entity.data);
		const { added, duplicates, failed } = await addMentionsToBoard({
			boardId: board.id,
			mentions,
			appKind,
			projectId: props.projectId,
		});

		boards.invalidateBoardsList();
		boards.invalidateClipMembership();

		if (added === 0) {
			toast.error({
				title:
					duplicates > 0
						? "Those clips are already on that board"
						: "Failed to add clips to board",
			});
			return;
		}

		const skipped = [
			duplicates > 0 ? `${duplicates} already on the board` : "",
			failed > 0 ? `${failed} could not be added` : "",
		].filter(Boolean);
		toast.success({
			symbol: "bookmark_add",
			title: `Added ${added} ${added === 1 ? "clip" : "clips"} to "${board.title ?? "board"}"`,
			description: skipped.length > 0 ? skipped.join(", ") : undefined,
		});
	};

	return (
		<Card class="clipboard-view">
			<VStack spacing="medium">
				<HStack fullWidth justify="space-between" align="center">
					<HStack spacing="medium" align="center">
						<MaterialSymbol symbol="content_paste" color="fg2" />
						<TextMono weight="bold" transform="uppercase" color="fg2">
							Clipboard
						</TextMono>
						<Counter value={clipboardCount()} theme="primary" size="small" />
					</HStack>
					<HStack spacing="medium" align="stretch">
						<Show when={props.onPasteContext}>
							{(pasteContext) => (
								<Button
									symbol="forum"
									variant="secondary"
									onMouseDown={(event) => event.preventDefault()}
									onClick={() => pasteContext()()}
								>
									Paste context
								</Button>
							)}
						</Show>
						<Show when={props.canAddToBoard !== false}>
							<TooltipTrigger>
								<Button
									symbol="bookmark_add"
									symbolRelativeSize="even-larger"
									variant="secondary"
									aria-label="Add to board"
									shape="square"
									onMouseDown={(event) => event.preventDefault()}
									onClick={() => setBoardPanelOpen(true)}
								/>
								<Tooltip>Add to board</Tooltip>
							</TooltipTrigger>
						</Show>
						<Show when={askPage}>
							{(href) => (
								<TooltipTrigger>
									<Button
										symbol="add_comment"
										symbolRelativeSize="even-larger"
										variant="secondary"
										aria-label="Start new question with clipboard contents"
										shape="square"
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => startNewQuestion(href())}
									/>
									<Tooltip>Start new question</Tooltip>
								</TooltipTrigger>
							)}
						</Show>
						<Show when={appKind === "studio"}>
							<TooltipTrigger>
								<Button
									symbol="download"
									symbolRelativeSize="even-larger"
									variant="secondary"
									aria-label="Download clip list as CSV"
									shape="square"
									onMouseDown={(event) => event.preventDefault()}
									onClick={downloadClipList}
								/>
								<Tooltip>Download clip list (CSV)</Tooltip>
							</TooltipTrigger>
						</Show>
						<TooltipTrigger>
							<Button
								symbol="close"
								symbolRelativeSize="even-larger"
								variant="ghost"
								theme="monochrome"
								aria-label="Clear clipboard"
								shape="square"
								onClick={clearClipboard}
							/>
							<Tooltip>Clear clipboard</Tooltip>
						</TooltipTrigger>
					</HStack>
				</HStack>
				<Stack direction="row" spacing="small" align="center" wrap>
					<For each={clipboardVideoMentions()}>
						{(entity) => (
							<VideoMentionPill
								mention={entity.data}
								tone="neutral"
								onRemove={() => removeVideoMentionFromClipboard(entity.data)}
							/>
						)}
					</For>
				</Stack>
			</VStack>
			<Show when={boardPanelOpen()}>
				<SelectBoardPanel
					isOpen={boardPanelOpen}
					setIsOpen={setBoardPanelOpen}
					title="Add to board"
					confirmLabel="Add to board"
					projectId={props.projectId}
					onConfirm={addClipboardToBoard}
				/>
			</Show>
		</Card>
	);
};
